<template>
    <div class="row items-center">
        <div class="col-12">
            <q-select
                v-model="product_category"
                @clear="product_category = null"
                :options="productCategories"
                :label="$t('Product Category')"
                :rules="[val => val !== '' || $t('Select a product category')]"
                bg-color="white"
                color="accent"
                outlined
                clearable
                stack-label
                @update:model-value="assignCategory()"
            />
        </div>
    </div>
    <div
        v-if="product_category"
        class="row q-mb-md justify-start assign-property"
    >
        <div v-if="selectedProperties.length > 0" class="q-pt-md">
            <q-chip
                v-for="(property, index) in selectedProperties"
                :key="index"
                class="q-mb-md q-pa-md"
                outline
                square
                color="secondary"
                text-color="white"
                icon="fa-solid fa-tag"
                removable
                @remove="removeProductProperty(index)"
            >
                {{ property.product_property.label }}:
                {{ property.product_property_allowed_value.label }}
                &nbsp;
            </q-chip>
        </div>
        <q-btn
            flat
            dense
            outline
            color="secondary"
            :label="$t('Assign property')"
            :ripple="false"
            icon="fa-solid fa-plus"
        >
            <q-menu style="width: 200px">
                <q-list class="q-pa-none">
                    <q-item>
                        <q-item-section>
                            <q-item-label>{{
                                $t('Choose a property')
                            }}</q-item-label>
                            <q-select
                                v-model="selectedProductProperty"
                                class="q-mt-xs"
                                :label="$t('Property')"
                                :options="productPropertyOptions"
                                behavior="menu"
                                bg-color="white"
                                outlined
                                lazy-rules
                                :disable="product_category === ''"
                            />
                        </q-item-section>
                    </q-item>
                    <q-item>
                        <q-item-section>
                            <q-item-label>{{
                                $t('Choose a value')
                            }}</q-item-label>
                            <q-select
                                v-model="selectedProductPropertyAllowedValue"
                                :label="$t('Value')"
                                class="q-mt-xs q-mb-md"
                                :options="propertyAllowedValueOptions"
                                @update:model-value="
                                    updateProductPropertyAllowedValues
                                "
                                behavior="menu"
                                bg-color="white"
                                outlined
                                lazy-rules
                                :disable="selectedProductProperty === ''"
                            >
                                <template v-slot:no-option>
                                    <q-item>
                                        <q-item-section class="text-grey">
                                            {{ $t('No results') }}
                                        </q-item-section>
                                    </q-item>
                                </template>
                            </q-select>
                        </q-item-section>
                    </q-item>
                </q-list>
            </q-menu>
        </q-btn>
    </div>
</template>
<script>
import { mapState } from 'pinia';
import { useProductsStore } from '@stores/products.js';

export default {
    name: 'ProductProperty',
    emits: ['update:product-category', 'update:product-property'],
    mounted() {
        this.productCategories = this.getProductCategories.reduce(
            (result, category) => {
                result.push({ label: category.name, value: category.id });
                return result;
            },
            []
        );
    },
    props: {
        category: {
            type: Number,
            default: null
        },
        properties: {
            type: String
        }
    },
    watch: {
        /**
         * Watcher for the selected category received from api calls
         * @param {string} value - id of the assigned category
         */
        category(id) {
            const productCategory = this.getProductCategories.find(
                category => category.id === id
            );
            this.product_category = productCategory
                ? { label: productCategory.name, value: productCategory.id }
                : null;
        },
        /**
         * Watcher for the selected properties received from api calls
         * @param {array} value - the selected properties
         */
        properties(value) {
            if (value) {
                this.selectedProperties = JSON.parse(value).reduce(
                    (result, property) => {
                        if (
                            property.product_property === null ||
                            property.product_property_allowed_value === null
                        ) {
                            return result;
                        }
                        result.push({
                            product_property: {
                                label: this.getPropertyName(
                                    property.product_property
                                ),
                                value: property.product_property
                            },
                            product_property_allowed_value: {
                                label: this.getAllowedValueName(
                                    property.product_property_allowed_value
                                ),
                                value: property.product_property_allowed_value
                            }
                        });
                        return result;
                    },
                    []
                );
            }
        },
        /**
         * Set the allowed values for the selected product property
         * @param {*} value
         */
        selectedProductProperty(value) {
            this.propertyAllowedValueOptions = this.getProductProperties
                .filter(property => property.id === value.value)
                .flatMap(property =>
                    property.allowed_values.map(value => ({
                        label: value.value,
                        value: value.id
                    }))
                );
        }
    },
    data() {
        return {
            product_category: null,
            productCategories: [],
            productProperties: null,
            selectedProductProperty: '',
            selectedProductPropertyAllowedValue: '',
            selectedProperties: [],
            propertyAllowedValueOptions: []
        };
    },
    computed: {
        ...mapState(useProductsStore, [
            'getProductCategories',
            'getProductProperties'
        ]),
        /**
         * Get the options for the product properties dropdown
         * based on the selected product category
         * @returns {array} - the product properties
         * for the selected product category
         */
        productPropertyOptions() {
            return this.getProductProperties
                .filter(
                    property =>
                        property.product_category ===
                        this.product_category.value
                )
                .reduce((result, property) => {
                    result.push({ label: property.name, value: property.id });
                    return result;
                }, []);
        }
    },
    methods: {
        /**
         * Update the product property allowed values
         * @param {object} updated_property - the updated product property
         */
        updateProductPropertyAllowedValues(updated_property) {
            const propertyExists = this.selectedProperties.some(
                property =>
                    property.product_property.label ===
                    this.selectedProductProperty.label
            );
            if (propertyExists) {
                // Update the value of the existing object
                this.selectedProperties = this.selectedProperties.map(
                    property => {
                        if (
                            property.product_property.label ===
                            this.selectedProductProperty.label
                        ) {
                            property.product_property_allowed_value.label =
                                updated_property.label;
                            property.product_property_allowed_value.value =
                                updated_property.value;
                        }
                        return property;
                    }
                );
            } else {
                this.selectedProperties.push({
                    product_property: this.selectedProductProperty,
                    product_property_allowed_value: updated_property
                });
            }
            this.selectedProductPropertyAllowedValue = '';
            this.selectedProductProperty = '';
            this.$emit('update:product-property', this.selectedProperties);
        },
        /**
         * Assign the product category
         * @param {object} category - the selected product category
         */
        assignCategory() {
            // if a product category is changed so should the properties
            // since the properties are dependent on the category
            this.$emit('update:product-category', this.product_category);
            this.selectedProperties = [];
            this.$emit('update:product-property', []);
        },
        /**
         * Remove the product property from the selected properties
         * @param {integer} index
         */
        removeProductProperty(index) {
            this.selectedProperties.splice(index, 1);
            this.$emit('update:product-property', this.selectedProperties);
        },
        /**
         * Set the product properties for the selected product category in the dropdown
         * @param {object} productCategory - the selected product category
         */
        setProductProperties(productCategory) {
            this.productProperties = this.getProductProperties
                .filter(
                    productProperty =>
                        productProperty.product_category === productCategory.id
                )
                .reduce((result, property) => {
                    result[property.id] = property.allowed_values;
                    return result;
                }, {});
        },
        /**
         * Remove the product category
         * and reset the product properties
         */
        removeProductCategory() {
            this.product_category = null;
            this.productProperties = null;
            this.selectedProperties = [];
            this.$emit('update:product-category', null);
            this.$emit('update:product-property', []);
        },
        /**
         * Get the name of product property from the id
         * @param {integer} propertyId
         * @return {string} property name
         */
        getPropertyName(propertyId) {
            return this.getProductProperties.reduce((result, property) => {
                if (property.id === propertyId) {
                    return property.name;
                }
                return result;
            }, '');
        },
        /**
         * Get the name of the allowed value from the id
         * @param {integer} allowedValueId
         * @return {string} allowed value name
         */
        getAllowedValueName(allowedValueId) {
            return this.getProductProperties.reduce((result, property) => {
                const allowedValues = property.allowed_values;
                for (let index = 0; index < allowedValues.length; index++) {
                    if (allowedValues[index].id === allowedValueId) {
                        return allowedValues[index].value;
                    }
                }
                return result;
            }, '');
        }
    }
};
</script>
<style lang="scss" scoped>
// disable hover background on assign property button
:deep(.q-btn .q-focus-helper) {
    display: none;
}
</style>
